import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { FOUNDER_RAIL, SQUAD_ROLES } from '../data/squad.js';

// ─── 3D Math — Canvas 2D painter’s algorithm ───
const rotY = (v, a) => {
  const c = Math.cos(a);
  const s = Math.sin(a);
  return { x: v.x * c + v.z * s, y: v.y, z: -v.x * s + v.z * c };
};

const project = (v, fov, cx, cy, sc) => {
  const p = fov / (fov + v.z);
  return { x: v.x * p * sc + cx, y: -v.y * p * sc + cy, z: v.z };
};

const sub = (a, b) => ({ x: a.x - b.x, y: a.y - b.y, z: a.z - b.z });
const cross = (a, b) => ({
  x: a.y * b.z - a.z * b.y,
  y: a.z * b.x - a.x * b.z,
  z: a.x * b.y - a.y * b.x,
});
const dot = (a, b) => a.x * b.x + a.y * b.y + a.z * b.z;
const norm = (v) => {
  const l = Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z);
  return l ? { x: v.x / l, y: v.y / l, z: v.z / l } : v;
};
const faceNormal = (vs) => norm(cross(sub(vs[1], vs[0]), sub(vs[2], vs[0])));
const lerp3 = (a, b, t) => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
  z: a.z + (b.z - a.z) * t,
});

const VERTS = {
  b0: { x: -50, y: -240, z: -50 },
  b1: { x: 50, y: -240, z: -50 },
  b2: { x: 50, y: -240, z: 50 },
  b3: { x: -50, y: -240, z: 50 },
  t0: { x: -24, y: 130, z: -24 },
  t1: { x: 24, y: 130, z: -24 },
  t2: { x: 24, y: 130, z: 24 },
  t3: { x: -24, y: 130, z: 24 },
  a: { x: 0, y: 158, z: 0 },
};

const FACES = [
  { k: ['b0', 'b3', 'b2', 'b1'], gold: false },
  { k: ['b0', 'b1', 't1', 't0'], gold: false },
  { k: ['b1', 'b2', 't2', 't1'], gold: false },
  { k: ['b2', 'b3', 't3', 't2'], gold: false },
  { k: ['b3', 'b0', 't0', 't3'], gold: false },
  { k: ['t0', 't1', 'a'], gold: true },
  { k: ['t1', 't2', 'a'], gold: true },
  { k: ['t2', 't3', 'a'], gold: true },
  { k: ['t3', 't0', 'a'], gold: true },
];

const KEY = norm({ x: 1.5, y: 2.0, z: -1.5 });
const FILL = norm({ x: -0.8, y: 0.5, z: -0.3 });
const RIM = norm({ x: 0.0, y: 0.4, z: 1.5 });

const SHAFT_QUADS = [
  ['b0', 'b1', 't1', 't0'],
  ['b1', 'b2', 't2', 't1'],
  ['b2', 'b3', 't3', 't2'],
  ['b3', 'b0', 't0', 't3'],
];

const ALL_EDGES = [
  ['b0', 'b1'],
  ['b1', 'b2'],
  ['b2', 'b3'],
  ['b3', 'b0'],
  ['b0', 't0'],
  ['b1', 't1'],
  ['b2', 't2'],
  ['b3', 't3'],
  ['t0', 't1'],
  ['t1', 't2'],
  ['t2', 't3'],
  ['t3', 't0'],
  ['t0', 'a'],
  ['t1', 'a'],
  ['t2', 'a'],
  ['t3', 'a'],
];

const GRID_COLS = 4;
const GRID_ROWS = 9;

const SEAM_EDGES = new Set(['t0-t1', 't1-t2', 't2-t3', 't3-t0']);

const CANVAS_V_PAD = 20;
const CANVAS_H_PAD = 24;
const FOV_NUM = 520;

/** Screen-space bounds of all vertices at cy=0 (used to fit scale + center). */
function vertexScreenBounds(angle, sc, cx) {
  let minY = Infinity;
  let maxY = -Infinity;
  let minX = Infinity;
  let maxX = -Infinity;
  for (const key of Object.keys(VERTS)) {
    const rv = rotY(VERTS[key], angle);
    const pt = project(rv, FOV_NUM, cx, 0, sc);
    if (pt.y < minY) minY = pt.y;
    if (pt.y > maxY) maxY = pt.y;
    if (pt.x < minX) minX = pt.x;
    if (pt.x > maxX) maxX = pt.x;
  }
  return {
    minY,
    maxY,
    spanY: maxY - minY,
    minX,
    maxX,
    spanX: maxX - minX,
  };
}

/**
 * Uniform scale + vertical center so hull fits with padding; reserves space for bob (±4sc).
 * Returns fitted sc and cyBase (add bobDelta before drawing).
 */
function computeCameraFit(angle, H, W) {
  const cx = W / 2;
  let sc = Math.min(2.15, H / 420) * 0.78;

  for (let iter = 0; iter < 4; iter++) {
    const bobAmt = 4 * sc;
    const budgetY = H - 2 * CANVAS_V_PAD - 2 * bobAmt;
    const budgetX = W - 2 * CANVAS_H_PAD;
    const b = vertexScreenBounds(angle, sc, cx);
    if (b.spanY <= 0 || b.spanX <= 0 || budgetY <= 0) break;

    let scale = 1;
    if (b.spanY > budgetY) scale = Math.min(scale, budgetY / b.spanY);
    if (b.spanX > budgetX) scale = Math.min(scale, budgetX / b.spanX);
    if (scale < 1) sc *= scale;
    else break;
  }

  const b = vertexScreenBounds(angle, sc, cx);
  const cyBase = H / 2 - (b.minY + b.maxY) / 2;
  return { sc, cx, cyBase };
}

function createMote() {
  const spread = 65;
  const spawnY = -240 + Math.random() * 420;
  const yFrac = (spawnY + 240) / 398;
  const lateralSpread = spread * (1 - yFrac * 0.6);
  return {
    x: (Math.random() - 0.5) * lateralSpread,
    y: spawnY,
    z: (Math.random() - 0.5) * lateralSpread,
    vx: (Math.random() - 0.5) * 0.15,
    vy: 0.15 + Math.random() * 0.3,
    life: 0,
    maxLife: 80 + Math.random() * 120,
    size: 0.4 + Math.random() * 0.8,
    brightness: 0.15 + Math.random() * 0.25,
  };
}

function rgbFromComputed(el) {
  if (!el) return null;
  const c = getComputedStyle(el).color;
  const m = /^rgba?\((\d+),\s*(\d+),\s*(\d+)/.exec(c);
  if (!m) return null;
  return { r: +m[1], g: +m[2], b: +m[3] };
}

function rgba(rgb, a) {
  return `rgba(${rgb.r},${rgb.g},${rgb.b},${a})`;
}

/**
 * End each horizontal segment on the canvas column’s rail-adjacent edge, inset from wrap.right —
 * lines stop short of the obelisk and do not hug the outer band edge.
 */
const CONNECTOR_STOP_INSET_FROM_WRAP_RIGHT_PX = 28;

/**
 * Squad instrument: canvas + role rail + static SVG connectors from role titles toward the obelisk.
 * Motion (DESIGN.md § Squad obelisk): camera fit inside canvas (scale + center, bob reserve),
 * spin, bob, particles.
 */
export function SquadObelisk() {
  const bandRef = useRef(null);
  const founderTitleRef = useRef(null);
  const productTitleRef = useRef(null);
  const marketingTitleRef = useRef(null);
  const techTitleRef = useRef(null);

  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const signalProbeRef = useRef(null);
  const goldProbeRef = useRef(null);
  const angleRef = useRef(0);
  const frameRef = useRef(0);
  const startTimeRef = useRef(0);
  const motesRef = useRef([]);
  const draggingRef = useRef(false);
  const lastPointerXRef = useRef(0);
  const reducedMotionRef = useRef(false);
  const rafRef = useRef(0);
  const cancelledRef = useRef(false);
  const redrawStaticRef = useRef(null);

  const [connectorLines, setConnectorLines] = useState(null);

  useLayoutEffect(() => {
    const measureConnectors = () => {
      const band = bandRef.current;
      const wrap = wrapRef.current;
      const mq = window.matchMedia('(min-width: 900px)');
      const titleEls = [
        founderTitleRef.current,
        productTitleRef.current,
        marketingTitleRef.current,
        techTitleRef.current,
      ];
      if (!band || !wrap || titleEls.some((el) => !el) || !mq.matches) {
        setConnectorLines(null);
        return;
      }
      const br = band.getBoundingClientRect();
      const wr = wrap.getBoundingClientRect();
      /** Rail-adjacent edge of the canvas column — lines do not cross into the obelisk draw area. */
      const xEnd = wr.right - br.left - CONNECTOR_STOP_INSET_FROM_WRAP_RIGHT_PX;

      const lines = titleEls.map((el, i) => {
        const tr = el.getBoundingClientRect();
        const y = tr.top + tr.height / 2 - br.top;
        const xTitle = tr.left - br.left;
        return { xCanvas: xEnd, xTitle, y, apex: i === 0 };
      });
      setConnectorLines(lines);
    };

    measureConnectors();
    const ro = new ResizeObserver(measureConnectors);
    const band = bandRef.current;
    const wrap = wrapRef.current;
    if (band) ro.observe(band);
    if (wrap) ro.observe(wrap);
    window.addEventListener('resize', measureConnectors);
    const mq = window.matchMedia('(min-width: 900px)');
    mq.addEventListener('change', measureConnectors);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measureConnectors);
      mq.removeEventListener('change', measureConnectors);
    };
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    cancelledRef.current = false;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    reducedMotionRef.current = mq.matches;
    startTimeRef.current = performance.now();

    if (!motesRef.current.length) {
      for (let i = 0; i < 20; i++) {
        const m = createMote();
        m.life = Math.random() * m.maxLife;
        motesRef.current.push(m);
      }
    }

    const drawScene = ({ shimmer, bobDeltaFactor, particles }) => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const signalRgb = rgbFromComputed(signalProbeRef.current) || { r: 255, g: 77, b: 0 };
      const goldRgb = rgbFromComputed(goldProbeRef.current) || { r: 212, g: 168, b: 74 };

      const dpr = window.devicePixelRatio || 1;
      const W = wrap.offsetWidth;
      const H = wrap.offsetHeight;

      if (W < 2 || H < 2) return;

      if (canvas.width !== W * dpr || canvas.height !== H * dpr) {
        canvas.width = W * dpr;
        canvas.height = H * dpr;
      }

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);

      const angle = angleRef.current;
      const { sc, cx, cyBase } = computeCameraFit(angle, H, W);
      const bobDelta = bobDeltaFactor * 4 * sc;
      const cy = cyBase + bobDelta;

      const rv = {};
      const pv = {};
      for (const key of Object.keys(VERTS)) {
        rv[key] = rotY(VERTS[key], angle);
        pv[key] = project(rv[key], FOV_NUM, cx, cy, sc);
      }

      const faceData = FACES.map((f) => {
        const verts3d = f.k.map((k) => rv[k]);
        const vertsP = f.k.map((k) => pv[k]);
        const avgZ = verts3d.reduce((s, v) => s + v.z, 0) / verts3d.length;
        const n = faceNormal(verts3d);
        const key_d = Math.max(0, dot(n, KEY));
        const fill_d = Math.max(0, dot(n, FILL)) * 0.22;
        const rim_d = Math.max(0, dot(n, RIM)) * 0.08;
        const intensity = key_d + fill_d + rim_d;
        return { ...f, vertsP, avgZ, intensity };
      });

      faceData.sort((a, b) => a.avgZ - b.avgZ);

      /* Keep ground glow inside canvas — unclamped ellipse extended past bottom when H is large. */
      const glowYRaw = cy + 200 * sc;
      const glowRy = 22 * sc;
      const glowY = Math.min(glowYRaw, H - CANVAS_V_PAD - glowRy);
      const glowRx = Math.min(100 * sc, W * 0.42);
      const glow = ctx.createRadialGradient(cx, glowY, 0, cx, glowY, glowRx);
      glow.addColorStop(0, rgba(goldRgb, 0.1));
      glow.addColorStop(0.6, rgba(goldRgb, 0.03));
      glow.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = glow;
      ctx.beginPath();
      ctx.ellipse(cx, glowY, glowRx, glowRy, 0, 0, Math.PI * 2);
      ctx.fill();

      for (const { gold, vertsP, intensity } of faceData) {
        ctx.beginPath();
        vertsP.forEach((v, i) => (i === 0 ? ctx.moveTo(v.x, v.y) : ctx.lineTo(v.x, v.y)));
        ctx.closePath();

        if (gold) {
          const t = intensity * (0.68 + shimmer * 0.32);
          const r = Math.min(255, Math.round(55 + t * 205));
          const g = Math.min(255, Math.round(32 + t * 155));
          const b = Math.min(255, Math.round(3 + t * 48));
          ctx.fillStyle = `rgb(${r},${g},${b})`;
        } else {
          const r = Math.round(7 + intensity * 48);
          const g = Math.round(7 + intensity * 44);
          const b = Math.round(4 + intensity * 38);
          ctx.fillStyle = `rgb(${r},${g},${b})`;
        }
        ctx.fill();
      }

      ctx.lineWidth = 1.2;
      const gridStroke = rgba(signalRgb, 0.9);
      for (const [k0, k1, k2, k3] of SHAFT_QUADS) {
        const A = rv[k0];
        const B = rv[k1];
        const C = rv[k2];
        const D = rv[k3];
        const n = faceNormal([A, B, C]);
        if (n.z <= 0) continue;

        for (let row = 1; row < GRID_ROWS; row++) {
          if (row === 3 || row === 6) continue;
          const v = row / GRID_ROWS;
          const lp = project(lerp3(A, D, v), FOV_NUM, cx, cy, sc);
          const rp = project(lerp3(B, C, v), FOV_NUM, cx, cy, sc);
          ctx.beginPath();
          ctx.moveTo(lp.x, lp.y);
          ctx.lineTo(rp.x, rp.y);
          ctx.strokeStyle = gridStroke;
          ctx.stroke();
        }
        for (let col = 1; col < GRID_COLS; col++) {
          const u = col / GRID_COLS;
          const bp = project(lerp3(A, B, u), FOV_NUM, cx, cy, sc);
          const tp = project(lerp3(D, C, u), FOV_NUM, cx, cy, sc);
          ctx.beginPath();
          ctx.moveTo(bp.x, bp.y);
          ctx.lineTo(tp.x, tp.y);
          ctx.strokeStyle = gridStroke;
          ctx.stroke();
        }
      }

      const ap = pv.a;
      const apGlow = ctx.createRadialGradient(ap.x, ap.y, 0, ap.x, ap.y, 48 * sc);
      apGlow.addColorStop(0, `rgba(255,225,80,${0.5 + shimmer * 0.15})`);
      apGlow.addColorStop(0.3, `rgba(255,195,50,${0.25 + shimmer * 0.1})`);
      apGlow.addColorStop(0.6, `rgba(255,160,30,${0.08 + shimmer * 0.05})`);
      apGlow.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = apGlow;
      ctx.beginPath();
      ctx.arc(ap.x, ap.y, 48 * sc, 0, Math.PI * 2);
      ctx.fill();

      for (const [k1, k2] of ALL_EDGES) {
        const p1 = pv[k1];
        const p2 = pv[k2];
        const edgeKey = `${k1}-${k2}`;
        const isSeam = SEAM_EDGES.has(edgeKey);
        ctx.beginPath();
        ctx.moveTo(p1.x, p1.y);
        ctx.lineTo(p2.x, p2.y);
        ctx.strokeStyle = isSeam ? rgba(goldRgb, 0.85) : gridStroke;
        ctx.lineWidth = isSeam ? 2.0 : 1.2;
        ctx.stroke();
      }

      const DIVIDER_ROWS = [3, 6];
      for (const [k0, k1, k2, k3] of SHAFT_QUADS) {
        const A2 = rv[k0];
        const B2 = rv[k1];
        const C2 = rv[k2];
        const D2 = rv[k3];
        const n2 = faceNormal([A2, B2, C2]);
        if (n2.z <= 0) continue;
        for (const row of DIVIDER_ROWS) {
          const t = row / GRID_ROWS;
          const lp2 = project(lerp3(A2, D2, t), FOV_NUM, cx, cy, sc);
          const rp2 = project(lerp3(B2, C2, t), FOV_NUM, cx, cy, sc);
          ctx.beginPath();
          ctx.moveTo(lp2.x, lp2.y);
          ctx.lineTo(rp2.x, rp2.y);
          ctx.strokeStyle = rgba(goldRgb, 0.7);
          ctx.lineWidth = 2.0;
          ctx.stroke();
        }
      }

      if (particles && motesRef.current.length) {
        const motes = motesRef.current;
        for (const m of motes) {
          m.x += m.vx;
          m.y += m.vy;
          m.life += 1;
          if (m.life > m.maxLife) Object.assign(m, createMote());
          const lifeFrac = m.life / m.maxLife;
          const fadeAlpha =
            lifeFrac < 0.15 ? lifeFrac / 0.15 : lifeFrac > 0.7 ? (1 - lifeFrac) / 0.3 : 1;
          const alpha = m.brightness * fadeAlpha;
          if (alpha <= 0) continue;
          const p = project(rotY({ x: m.x, y: m.y, z: m.z }, angle), FOV_NUM, cx, cy, sc);
          ctx.beginPath();
          ctx.arc(p.x, p.y, m.size * sc, 0, Math.PI * 2);
          ctx.fillStyle = rgba(goldRgb, alpha);
          ctx.fill();
        }
      }

    };

    const drawStatic = () => {
      drawScene({
        shimmer: 0.5,
        bobDeltaFactor: 0,
        particles: false,
      });
    };

    const loop = () => {
      if (cancelledRef.current || reducedMotionRef.current) return;
      const elapsed = (performance.now() - startTimeRef.current) / 1000;
      const frame = frameRef.current;
      const shimmer = Math.sin(frame * 0.016) * 0.5 + 0.5;

      angleRef.current += 0.005;
      frameRef.current += 1;

      drawScene({
        shimmer,
        bobDeltaFactor: Math.sin(elapsed * 0.6),
        particles: true,
      });

      rafRef.current = requestAnimationFrame(loop);
    };

    const onPointerDown = (e) => {
      if (reducedMotionRef.current) return;
      draggingRef.current = true;
      lastPointerXRef.current = e.clientX;
      canvas.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e) => {
      if (reducedMotionRef.current || !draggingRef.current) return;
      const dx = e.clientX - lastPointerXRef.current;
      angleRef.current += dx * 0.005;
      lastPointerXRef.current = e.clientX;
    };

    const endDrag = (e) => {
      draggingRef.current = false;
      try {
        if (e && canvas.hasPointerCapture(e.pointerId)) canvas.releasePointerCapture(e.pointerId);
      } catch (_) {}
    };

    const onMq = () => {
      reducedMotionRef.current = mq.matches;
      cancelAnimationFrame(rafRef.current);
      if (mq.matches) {
        drawStatic();
      } else {
        startTimeRef.current = performance.now();
        frameRef.current = 0;
        rafRef.current = requestAnimationFrame(loop);
      }
    };

    canvas.addEventListener('pointerdown', onPointerDown);
    canvas.addEventListener('pointermove', onPointerMove);
    canvas.addEventListener('pointerup', endDrag);
    canvas.addEventListener('pointercancel', endDrag);

    mq.addEventListener('change', onMq);

    const ro = new ResizeObserver(() => {
      if (reducedMotionRef.current) drawStatic();
    });
    ro.observe(wrap);

    redrawStaticRef.current = drawStatic;

    if (mq.matches) {
      drawStatic();
    } else {
      rafRef.current = requestAnimationFrame(loop);
    }

    return () => {
      cancelledRef.current = true;
      redrawStaticRef.current = null;
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      mq.removeEventListener('change', onMq);
      canvas.removeEventListener('pointerdown', onPointerDown);
      canvas.removeEventListener('pointermove', onPointerMove);
      canvas.removeEventListener('pointerup', endDrag);
      canvas.removeEventListener('pointercancel', endDrag);
    };
  }, []);

  const mqReduce =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const allowDrag = !mqReduce;

  return (
      <div
        id="squad-instrument"
        className="squad-obelisk-zone"
        role="region"
        aria-label={
          allowDrag
            ? 'Squad instrument: Founder at the apex; Product, Marketing, and Tech mapped to shaft thirds. Drag to rotate.'
            : 'Squad instrument: Founder at the apex; Product, Marketing, and Tech mapped to shaft thirds.'
        }
        style={{
          marginBottom: 2,
        }}
      >
      <div ref={bandRef} className="squad-obelisk-band squad-obelisk-band--borderless">
        <div ref={wrapRef} className="squad-obelisk-wrap">
          <span
            ref={signalProbeRef}
            aria-hidden
            style={{
              position: 'absolute',
              width: 0,
              height: 0,
              overflow: 'hidden',
              clip: 'rect(0,0,0,0)',
              color: 'var(--signal)',
            }}
          />
          <span
            ref={goldProbeRef}
            aria-hidden
            style={{
              position: 'absolute',
              width: 0,
              height: 0,
              overflow: 'hidden',
              clip: 'rect(0,0,0,0)',
              color: 'var(--verdict-cond)',
            }}
          />
          <canvas
            ref={canvasRef}
            role="presentation"
            aria-hidden
            style={{
              touchAction: 'none',
              cursor: allowDrag ? 'grab' : 'default',
            }}
          />
          {allowDrag ? (
            <div
              className="small"
              style={{
                position: 'absolute',
                bottom: 10,
                left: 16,
                fontFamily: 'var(--mono)',
                fontSize: 10,
                letterSpacing: 2,
                textTransform: 'uppercase',
                color: 'var(--fg-dim)',
                pointerEvents: 'none',
              }}
            >
              Drag to rotate
            </div>
          ) : null}
        </div>

        <div className="squad-obelisk-rail squad-obelisk-rail--graph">
          <article className="squad-rail-node squad-rail-node--apex">
            <div className="squad-rail-label squad-rail-label--apex">{FOUNDER_RAIL.bandLabel}</div>
            <div ref={founderTitleRef} className="squad-rail-title">
              {FOUNDER_RAIL.name}
            </div>
            <p className="small squad-rail-body">{FOUNDER_RAIL.body}</p>
          </article>
          {SQUAD_ROLES.map((r) => (
            <article key={r.kind} className={`squad-rail-node squad-rail-node--${r.kind}`}>
              <div className="squad-rail-label">{r.bandLabel}</div>
              <div
                ref={
                  r.kind === 'product'
                    ? productTitleRef
                    : r.kind === 'marketing'
                      ? marketingTitleRef
                      : techTitleRef
                }
                className="squad-rail-title"
              >
                {r.name}
              </div>
              <p className="small squad-rail-body">{r.body}</p>
            </article>
          ))}
        </div>

        {connectorLines?.length ? (
          <svg
            className="squad-connectors-svg"
            aria-hidden
          >
            {connectorLines.map((seg, i) => (
              <g key={i} style={{ color: seg.apex ? 'var(--verdict-cond)' : 'var(--signal)' }}>
                <line
                  x1={seg.xCanvas}
                  y1={seg.y}
                  x2={seg.xTitle}
                  y2={seg.y}
                  stroke="currentColor"
                  strokeOpacity={seg.apex ? 0.48 : 0.38}
                  strokeWidth={1.25}
                  strokeLinecap="round"
                  strokeDasharray="0 7"
                  vectorEffect="nonScalingStroke"
                />
                <circle
                  cx={seg.xCanvas}
                  cy={seg.y}
                  r={2.75}
                  fill="currentColor"
                  fillOpacity={seg.apex ? 0.55 : 0.42}
                />
              </g>
            ))}
          </svg>
        ) : null}
      </div>

    </div>
  );
}
